import type React from 'react';
import { Fragment, useCallback, useRef } from 'react';
import styled, { useTheme } from 'styled-components';
import { useIntl } from 'react-intl';

import { getGuideColors } from 'app/features/leads/components/guide/guide-colors';

import { GUIDE_STEP_COUNT, GUIDE_TAB_LABEL_IDS } from 'app/features/leads/components/guide/constants';
import { CircleCheckIcon } from 'app/features/leads/components/guide/guide-icons';
import Typography from 'app/ui/components/atoms/typography';

type GuideStepTabsProps = {
  currentStep: number;
  onStepSelect: (step: number) => void;
};

const TabsWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: ${({ theme: { spacing } }) => spacing(1)};
  padding: 0;
  width: 100%;

  ${({ theme: { media, spacing } }) =>
    media.medium(
      () => `
      padding: ${spacing(4)} 0 ${spacing(5)};
    `,
    )}
`;

const TabColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme: { spacing } }) => spacing(2)};
  width: 6.25rem;
  flex-shrink: 0;
  min-width: 0;

  ${({ theme: { media, spacing } }) =>
    media.medium(
      () => `
      gap: ${spacing(3)};
      width: 8.125rem;
    `,
    )}
`;

const TabButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme: { spacing } }) => spacing(2)};
  background: transparent;

  ${({ theme: { media, spacing } }) => media.medium(() => `gap: ${spacing(3)};`)}
  border: none;
  cursor: pointer;
  padding: 0;
  width: 100%;

  &:focus-visible {
    outline: 0.125rem solid ${({ theme }) => theme.semantic.accent_500};
    outline-offset: 0.25rem;
    border-radius: 0.25rem;
  }
`;

const StepBadge = styled.span<{ $variant: 'active' | 'upcoming' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 9999px;
  font-size: ${({ theme }) => theme.typography.small};
  font-weight: ${({ theme }) => theme.typography.bold};
  font-family: ${({ theme }) => theme.typography.poppins};

  ${({ theme: { media, typography } }) =>
    media.medium(
      () => `
      width: 2.5rem;
      height: 2.5rem;
      font-size: ${typography.xsmall};
    `,
    )}

  ${({ $variant, theme: { semantic } }) =>
    $variant === 'active'
      ? `
        background-color: ${semantic.accent_500};
        color: ${semantic.content_primary_button};
      `
      : `
        background-color: transparent;
        border: 0.0625rem solid ${semantic.neutrals_600};
        color: ${semantic.text_small_elements};
      `}
`;

const Connector = styled.div`
  width: 0.6875rem;
  height: 0.125rem;
  background-color: ${({ theme }) => theme.semantic.neutrals_400};
  flex-shrink: 0;
  margin-top: 1rem;

  ${({ theme: { media } }) =>
    media.medium(
      () => `
      width: 1.875rem;
      margin-top: 1.1875rem;
    `,
    )}
`;

const TabLabel = styled.span`
  font-weight: inherit;
  white-space: nowrap;
`;

const getTabState = (tabIndex: number, currentStep: number): 'completed' | 'active' | 'upcoming' => {
  if (tabIndex < currentStep) {
    return 'completed';
  }
  if (tabIndex === currentStep) {
    return 'active';
  }
  return 'upcoming';
};

export const GuideStepTabs: React.FC<GuideStepTabsProps> = ({ currentStep, onStepSelect }) => {
  const intl = useIntl();
  const { semantic } = useTheme();
  const guideColors = getGuideColors(semantic);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const tabsAriaLabel = intl.formatMessage({ id: 'leadsGuide.tabs.ariaLabel' });

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
      let nextIndex: number | null = null;

      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        nextIndex = (index + 1) % GUIDE_STEP_COUNT;
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        nextIndex = (index - 1 + GUIDE_STEP_COUNT) % GUIDE_STEP_COUNT;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = GUIDE_STEP_COUNT - 1;
      }

      if (nextIndex !== null) {
        event.preventDefault();
        onStepSelect(nextIndex);
        tabRefs.current[nextIndex]?.focus();
      }
    },
    [onStepSelect],
  );

  return (
    <TabsWrapper role="tablist" aria-label={tabsAriaLabel}>
      {Array.from({ length: GUIDE_STEP_COUNT }, (_, index) => {
        const state = getTabState(index, currentStep);
        const label = intl.formatMessage({ id: GUIDE_TAB_LABEL_IDS[index] });
        const isSelected = state === 'active';

        return (
          <Fragment key={GUIDE_TAB_LABEL_IDS[index]}>
            {index > 0 && <Connector aria-hidden={true} />}
            <TabColumn>
              <TabButton
                ref={(el) => {
                  tabRefs.current[index] = el;
                }}
                type="button"
                role="tab"
                id={`leads-guide-tab-${index}`}
                aria-selected={isSelected}
                aria-controls={`leads-guide-panel-${index}`}
                tabIndex={isSelected ? 0 : -1}
                data-testid={`leads-guide-tab-${index}`}
                onClick={() => onStepSelect(index)}
                onKeyDown={(event) => handleKeyDown(event, index)}
              >
                {state === 'completed' ? (
                  <CircleCheckIcon
                    size={32}
                    backgroundColor={semantic.success_400}
                    glyphColor={semantic.text_small_elements}
                  />
                ) : (
                  <StepBadge $variant={state === 'active' ? 'active' : 'upcoming'}>{index + 1}</StepBadge>
                )}
                <Typography variant="body3" color={isSelected ? guideColors.textPrimary : guideColors.textMuted}>
                  <TabLabel style={{ fontWeight: isSelected ? 600 : 400 }}>{label}</TabLabel>
                </Typography>
              </TabButton>
            </TabColumn>
          </Fragment>
        );
      })}
    </TabsWrapper>
  );
};
